import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/styles';
import { alpha } from '@material-ui/core/styles';
import {
  Avatar,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import ChatBubbleOutlineOutlinedIcon from '@material-ui/icons/ChatBubbleOutlineOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import FormatListBulletedOutlinedIcon from '@material-ui/icons/FormatListBulletedOutlined';
import InstagramIcon from '@material-ui/icons/Instagram';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  APP_PATH_AI,
  APP_PATH_DASHBOARD,
  APP_PATH_FAQ_EMPLOYERS,
  APP_PATH_FAQ_JOB_SEEKERS,
  APP_PATH_LOGIN,
  APP_PATH_MY_APPLICATIONS,
  APP_PATH_PRICING,
  APP_PATH_PRODUCTS,
  APP_PATH_ROOT,
  APP_PATH_SIGNUP,
  MAIN_APP_NAME
} from '../../config';
import Container from '@material-ui/core/Container';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { currentFileLogoUrlSelector } from '../../redux/features/file/filesSlice';
import { TypeAnimation } from 'react-type-animation';
import {
  trackHomepageCtaClick,
  trackTopbarClick
} from '../../helpers/analytics';
import Footer from '../../components/Footer';

const maxWidthMainHome = '1248px';
const paddingHeader = 85;
const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: 0
    }
  },
  shiftContent: {},
  content: {
    width: '100%',
    height: '100%',
    [theme.breakpoints.down('md')]: {
      marginTop: '0px'
    }
  },
  homeMainBackScene: {},
  coverSection: {
    marginTop: paddingHeader,
    backgroundColor: theme.palette.background.default,
    background: `
      radial-gradient(circle at 50% 0%, ${theme.palette.primary.main}12 0%, transparent 50%),
      radial-gradient(circle at 0% 100%, ${theme.palette.primary.main}08 0%, transparent 40%),
      radial-gradient(circle at 100% 100%, ${theme.palette.secondary.main}08 0%, transparent 40%),
      ${theme.palette.background.default}
    `,
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: `
      radial-gradient(${theme.palette.text.primary}15 1px, transparent 1px),
      radial-gradient(circle at 50% 0%, ${theme.palette.primary.main}12 0%, transparent 50%),
      radial-gradient(circle at 0% 100%, ${theme.palette.primary.main}08 0%, transparent 40%),
      radial-gradient(circle at 100% 100%, ${theme.palette.secondary.main}08 0%, transparent 40%)
    `,
    backgroundSize: '32px 32px, 100% 100%, 100% 100%, 100% 100%',
    backgroundPosition: '0 0, 0 0, 0 0, 0 0',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        linear-gradient(135deg,
          ${theme.palette.primary.main}10 0%,
          transparent 40%,
          transparent 60%,
          ${theme.palette.secondary.main}10 100%
        )
      `,
      pointerEvents: 'none'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '30%',
      background: `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 100%)`,
      pointerEvents: 'none'
    },
    [theme.breakpoints.down('md')]: {
      marginTop: 0,
      height: 'auto',
      paddingTop: '0px',
      paddingBottom: '40px',
      background: theme.palette.background.default,
      backgroundImage: 'none',
      '&::before': {
        display: 'none'
      },
      '&::after': {
        display: 'none'
      }
    }
  },
  coverSectionItems: {
    position: 'relative',
    backgroundColor: 'transparent',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    paddingTop: paddingHeader + 30,
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  coverSectionTextTitle: {
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginLeft: '50px',
    marginBottom: '20px',
    fontSize: '43px',
    lineHeight: '43px',
    zIndex: 1
  },
  coverSectionContentText: {
    color: theme.palette.text.primary,
    textAlign: 'center',
    fontSize: '56px',
    lineHeight: '64px',
    fontWeight: 600,
    [theme.breakpoints.down('md')]: {
      fontSize: '32px',
      lineHeight: '40px',
      height: '76px'
    }
  },
  coverSectionTextSubDesc: {
    fontSize: '20px',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: theme.spacing(4),
    maxWidth: '800px',
    lineHeight: '1.6',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: '16px',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  heroCTAContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3)
    }
  },
  primaryCTABtn: {
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 700,
    padding: theme.spacing(2, 5),
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
    color: theme.palette.common.white,
    boxShadow: `0 4px 14px rgba(0, 0, 0, 0.2)`,
    borderRadius: theme.spacing(3),
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: `0 6px 20px rgba(0, 0, 0, 0.3)`,
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      maxWidth: '300px',
      fontSize: '16px',
      padding: theme.spacing(1.5, 4)
    }
  },
  secondaryCTABtn: {
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 600,
    padding: theme.spacing(2, 5),
    background: 'transparent',
    color: theme.palette.text.primary,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(3),
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
      boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      maxWidth: '300px',
      fontSize: '16px',
      padding: theme.spacing(1.5, 4)
    }
  },
  platformSection: {
    padding: theme.spacing(8, 0, 10),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(6, 0, 8)
    }
  },
  platformSectionInner: {
    maxWidth: maxWidthMainHome,
    margin: '0 auto',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  platformEyebrow: {
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  platformTitle: {
    fontWeight: 700,
    fontSize: '2.4rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.9rem'
    }
  },
  platformTitleNoWrap: {
    whiteSpace: 'nowrap'
  },
  platformSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1.05rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(3)
  },
  platformFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gap: theme.spacing(2.25)
  },
  platformFeatureItem: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  platformFeatureTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    position: 'relative',
    paddingLeft: theme.spacing(2),
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      position: 'absolute',
      left: 0,
      top: '0.6em'
    }
  },
  platformFeatureTitleHighlight: {
    '&::before': {
      backgroundColor: theme.palette.success.main
    }
  },
  platformFeatureDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6
  },
  platformCtaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    flexWrap: 'wrap'
  },
  platformPrimaryCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(2),
    fontSize: '0.9rem'
  },
  platformSecondaryCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(2),
    fontSize: '0.9rem',
    border: `1px solid ${theme.palette.divider}`
  },
  platformHelperText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    lineHeight: 1.5
  },
  inlineLink: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  platformVisual: {
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2.5),
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)'
  },
  visualTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  visualTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.primary
  },
  visualBadge: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2)
  },
  visualColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)'
    }
  },
  visualColumn: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5)
  },
  visualColumnTitle: {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  visualMiniCard: {
    height: 34,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0
    }
  },
  visualScoreRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  visualScoreChip: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary
  },
  visualScoreChipStrong: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white
  },
  candidateSection: {
    padding: theme.spacing(2, 0, 10),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 0, 8)
    }
  },
  aiBannerSection: {
    padding: theme.spacing(0, 0, 6),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 0, 5)
    }
  },
  aiBannerCard: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)'
  },
  aiBannerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.text.secondary + '14',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(1.5)
  },
  aiBannerTitle: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
  },
  aiBannerDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(1)
  },
  aiBannerNote: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1.5)
  },
  aiBannerList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gap: theme.spacing(0.75)
  },
  aiBannerItem: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    lineHeight: 1.5,
    paddingLeft: theme.spacing(2),
    position: 'relative',
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      position: 'absolute',
      left: 0,
      top: '0.6em'
    }
  },
  aiBannerCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(2),
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider
  },
  candidateVisual: {
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2.5),
    boxShadow: '0 14px 36px rgba(15, 23, 42, 0.08)'
  },
  candidateVisualHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  candidateVisualTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.primary
  },
  candidateVisualTag: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2)
  },
  candidateVisualList: {
    display: 'grid',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  candidateVisualRow: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper
  },
  candidateVisualRowTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(0.5)
  },
  candidateVisualRowMeta: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  candidateVisualBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: theme.spacing(0.4, 1.2),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary
  },
  candidateVisualBadgePrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  candidateVisualMessage: {
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    lineHeight: 1.5
  },
  mainPage: {
    width: '100%',
    backgroundColor: theme.palette.background.default,
    padding: 0
  }
}));

const useStylesHeader = makeStyles((theme) => ({
  headerSection: {
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    position: 'fixed',
    top: 0,
    zIndex: 900,
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease',
    transform: 'translateY(0)',
    '&.header-hidden': {
      transform: 'translateY(-100%)',
      boxShadow: 'none'
    },
    '&.header-scrolled': {
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      backdropFilter: 'blur(10px)',
      backgroundColor: `${theme.palette.background.paper}f0`
    },
    [theme.breakpoints.down('md')]: {
      padding: 0,
      position: 'relative',
      width: 'auto',
      transform: 'none !important'
    }
  },
  headerSectionItems: {
    maxWidth: maxWidthMainHome,
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      padding: 0,
      margin: 0
    }
  },
  menuText: {
    color: theme.palette.text.secondary
  },
  menuTextLink: {
    textDecoration: 'none'
  },
  menuTextDropdown: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.text.primary
    }
  },
  dropdownMenu: {
    marginTop: theme.spacing(1)
  },
  dropdownMenuItem: {
    fontSize: '14px',
    padding: theme.spacing(1.5, 3),
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  infoMenuPaper: {
    borderRadius: 16,
    padding: theme.spacing(2.5),
    background: `
      linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)
    `,
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)'
  },
  communityMenuPaper: {
    width: 620,
    maxWidth: '92vw'
  },
  aboutMenuPaper: {
    width: 660,
    maxWidth: '92vw'
  },
  infoMenuContent: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr'
    }
  },
  infoMenuLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  infoMenuHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  infoMenuTitle: {
    fontWeight: 700
  },
  infoMenuSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem'
  },
  infoMenuItem: {
    borderRadius: 12,
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)'
    }
  },
  infoMenuItemTitle: {
    fontWeight: 600
  },
  infoMenuItemDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.82rem'
  },
  infoMenuIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    marginTop: theme.spacing(0.25)
  },
  infoMenuRight: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 220,
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto'
    }
  },
  infoMenuKicker: {
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    color: theme.palette.text.secondary
  },
  infoMenuSpotlightTitle: {
    fontWeight: 700
  },
  infoMenuSpotlightDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.88rem'
  },
  productsMenuPaper: {
    width: 860,
    maxWidth: '92vw',
    borderRadius: 16,
    padding: theme.spacing(3),
    background: `
      linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)
    `,
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)'
  },
  productsMenuContent: {
    display: 'grid',
    gridTemplateColumns: '2.2fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr'
    }
  },
  productsMenuLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  productsMenuHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  productsMenuTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  productsMenuSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    maxWidth: 520
  },
  productsMenuGrid: {
    marginTop: theme.spacing(1)
  },
  productsMenuItem: {
    borderRadius: 12,
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    whiteSpace: 'normal',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)'
    }
  },
  productsMenuItemTitle: {
    fontWeight: 600
  },
  productsMenuItemDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  productsMenuIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    marginTop: theme.spacing(0.25)
  },
  productsMenuRight: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  productsMenuKicker: {
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    color: theme.palette.text.secondary
  },
  productsMenuSpotlightTitle: {
    fontWeight: 700
  },
  productsMenuSpotlightDesc: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem'
  },
  productsMenuSpotlightButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 600
  },
  headerLogo: {
    width: 70,
    height: 70
  },
  postJobTextBtn: {
    marginLeft: theme.spacing(1)
  },
  headerBtn: {
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 700,
    padding: theme.spacing(1.5, 3),
    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.light} 90%)`,
    color: theme.palette.common.white,
    boxShadow: `0 3px 12px rgba(0, 0, 0, 0.15)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px rgba(0, 0, 0, 0.2)`,
      background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.secondary.main} 90%)`
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
      padding: theme.spacing(0.75, 1.5),
      minWidth: '44px'
    }
  },
  signInBtn: {
    textTransform: 'none',
    fontSize: '17px',
    fontWeight: 600,
    padding: theme.spacing(1.25, 2.5),
    background: 'transparent',
    color: theme.palette.text.primary,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(3),
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(90deg, transparent, ${theme.palette.primary.light}30, transparent)`,
      transition: 'left 0.5s ease'
    },
    '&:hover': {
      transform: 'translateY(-3px) scale(1.02)',
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
      boxShadow: `0 8px 25px ${theme.palette.primary.main}40`,
      borderColor: theme.palette.primary.main,
      '&::before': {
        left: '100%'
      }
    },
    '&:active': {
      transform: 'translateY(-1px) scale(0.98)'
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '13px',
      padding: theme.spacing(0.75, 1.5),
      minWidth: 'auto'
    }
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(0.5),
      flexWrap: 'nowrap'
    }
  },
  coverSectionTextTitle: {
    color: theme.palette.text.primary,
    textAlign: 'left',
    fontSize: '26px',
    lineHeight: '26px',
    fontWeight: 'bold',
    [theme.breakpoints.down('md')]: {
      fontSize: '20px',
      lineHeight: '20px'
    }
  },
  userProfileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  },
  userAvatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    backgroundColor: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '1rem'
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  userName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  userSubtext: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  mobileHeaderSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1),
    minHeight: 56
  },
  mobileMenuLinks: {
    display: 'none'
  },
  desktopMenuLinks: {
    display: 'flex'
  },
  mobileOnly: {
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },
  desktopOnly: {
    [theme.breakpoints.down('md')]: {
      display: 'none !important'
    }
  },
  mobileLogoLink: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto'
  },
  mobileLogo: {
    height: 40,
    width: 40,
    display: 'block',
    borderRadius: '50%',
    objectFit: 'cover'
  }
}));

const Header = ({ isDesktop }) => {
  const classes = useStylesHeader();
  const history = useHistory();
  const { isLogin, user } = useCurrentUserInfo();
  const companyLogoUrl = useSelector(currentFileLogoUrlSelector);
  const [scrollState, setScrollState] = React.useState({
    hidden: false,
    scrolled: false
  });
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const [aboutAnchorEl, setAboutAnchorEl] = React.useState(null);
  const [communityAnchorEl, setCommunityAnchorEl] = React.useState(null);
  const [productsAnchorEl, setProductsAnchorEl] = React.useState(null);

  const handleAboutClick = (event) => {
    trackTopbarClick('about_menu', '', 'about', 'open_menu');
    setAboutAnchorEl(event.currentTarget);
  };

  const handleAboutClose = () => {
    setAboutAnchorEl(null);
  };

  const handleCommunityClick = (event) => {
    trackTopbarClick('community_menu', '', 'community', 'open_menu');
    setCommunityAnchorEl(event.currentTarget);
  };

  const handleCommunityClose = () => {
    setCommunityAnchorEl(null);
  };

  const handleProductsClick = (event) => {
    trackTopbarClick('products_menu', '', 'products', 'open_menu');
    setProductsAnchorEl(event.currentTarget);
  };

  const handleProductsClose = () => {
    setProductsAnchorEl(null);
  };

  // Handle scroll to hide/show header
  React.useEffect(() => {
    if (!isDesktop) return; // Don't apply on mobile

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        setScrollState({ hidden: true, scrolled: true });
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setScrollState({ hidden: false, scrolled: currentScrollY > 50 });
      } else if (currentScrollY <= 50) {
        // At top
        setScrollState({ hidden: false, scrolled: false });
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isDesktop]);

  // Get user initials for avatar (fallback if no logo)
  const getUserInitials = (fullName) => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return fullName[0].toUpperCase();
  };

  const handleProfileClick = () => {
    // Redirect based on user role
    const isCandidate = user?.roles?.includes('ROLE_CANDIDATE');
    trackTopbarClick(
      'profile',
      isCandidate ? APP_PATH_MY_APPLICATIONS : APP_PATH_DASHBOARD,
      'account'
    );
    if (isCandidate) {
      history.push(APP_PATH_MY_APPLICATIONS);
    } else {
      history.push(APP_PATH_DASHBOARD);
    }
  };

  // Get role-specific text for the user profile section
  const getProfileSubtext = () => {
    const isCandidate = user?.roles?.includes('ROLE_CANDIDATE');
    return isCandidate ? 'My Applications →' : 'Go to Dashboard →';
  };

  const headerClasses = [
    classes.headerSection,
    scrollState.hidden && 'header-hidden',
    scrollState.scrolled && 'header-scrolled'
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={headerClasses}>
      {/* Desktop Layout */}
      {isDesktop ? (
        <Grid
          className={classes.headerSectionItems}
          container
          spacing={4}
          direction="row"
          alignItems="center"
          justifyContent="space-between">
          <Grid item>
            <RouterLink to={APP_PATH_ROOT} style={{ textDecoration: 'none' }}>
              <Grid
                container
                spacing={5}
                direction="row"
                alignItems="center"
                justifyContent="flex-start">
                <Grid item>
                  <Typography
                    className={classes.coverSectionTextTitle}
                    variant="h1">
                    {MAIN_APP_NAME}
                  </Typography>
                </Grid>
              </Grid>
            </RouterLink>
          </Grid>
          <Grid item>
            <Grid container spacing={8}>
              <Grid item>
                <Typography
                  className={classes.menuTextDropdown}
                  variant="h5"
                  onClick={handleProductsClick}>
                  Products ▾
                </Typography>
                <Menu
                  anchorEl={productsAnchorEl}
                  open={Boolean(productsAnchorEl)}
                  onClose={handleProductsClose}
                  className={classes.dropdownMenu}
                  PaperProps={{ className: classes.productsMenuPaper }}
                  MenuListProps={{ disablePadding: true }}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}>
                  <Box className={classes.productsMenuContent}>
                    <Box className={classes.productsMenuLeft}>
                      <div className={classes.productsMenuHeader}>
                        <Typography
                          variant="h6"
                          className={classes.productsMenuTitle}>
                          Hire
                        </Typography>
                        <Typography className={classes.productsMenuSubtitle}>
                          Everything you need to attract, evaluate, and move
                          candidates through your pipeline with speed and
                          clarity.
                        </Typography>
                      </div>
                      <Grid
                        container
                        spacing={2}
                        className={classes.productsMenuGrid}>
                        <Grid item xs={12} sm={6}>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'candidate_management',
                                `${APP_PATH_PRODUCTS}#candidate-management`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#candidate-management`
                              );
                              handleProductsClose();
                            }}>
                            <AssignmentTurnedInOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Candidate Management
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Visualize every stage with a clean kanban board.
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'application_tracking',
                                `${APP_PATH_PRODUCTS}#application-tracking`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#application-tracking`
                              );
                              handleProductsClose();
                            }}>
                            <FormatListBulletedOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Application Tracking
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Monitor submissions and review timelines at a
                                glance.
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'built_in_messaging',
                                `${APP_PATH_PRODUCTS}#built-in-messaging`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#built-in-messaging`
                              );
                              handleProductsClose();
                            }}>
                            <ChatBubbleOutlineOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Built-In Messaging
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Keep conversations connected to each candidate.
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'interview_scheduling',
                                `${APP_PATH_PRODUCTS}#interview-scheduling`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#interview-scheduling`
                              );
                              handleProductsClose();
                            }}>
                            <CalendarTodayOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Interview Scheduling
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Plan interviews with automated confirmations.
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'candidate_interview_scoring',
                                `${APP_PATH_PRODUCTS}#candidate-interview-scoring`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#candidate-interview-scoring`
                              );
                              handleProductsClose();
                            }}>
                            <ThumbUpAltOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Candidate Interview Scoring
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Track interview impressions with scorecard
                                history.
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem
                            className={classes.productsMenuItem}
                            onClick={() => {
                              trackTopbarClick(
                                'geo_location_filtering',
                                `${APP_PATH_PRODUCTS}#geo-location-filtering`,
                                'products'
                              );
                              history.push(
                                `${APP_PATH_PRODUCTS}#geo-location-filtering`
                              );
                              handleProductsClose();
                            }}>
                            <CheckCircleOutlineOutlinedIcon
                              className={classes.productsMenuIcon}
                            />
                            <Box>
                              <Typography
                                className={classes.productsMenuItemTitle}>
                                Geo-Location Filtering
                              </Typography>
                              <Typography
                                className={classes.productsMenuItemDesc}>
                                Focus on the regions that matter most.
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box className={classes.productsMenuRight}>
                      <Typography className={classes.productsMenuKicker}>
                        Spotlight
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.productsMenuSpotlightTitle}>
                        Candidate Interview Scoring
                      </Typography>
                      <Typography className={classes.productsMenuSpotlightDesc}>
                        Capture quick impressions after every interview and keep
                        a clear history of feedback for better, fairer
                        decisions.
                      </Typography>
                      <Button
                        color="primary"
                        className={classes.productsMenuSpotlightButton}
                        onClick={() => {
                          trackTopbarClick(
                            'products_spotlight',
                            `${APP_PATH_PRODUCTS}#candidate-interview-scoring`,
                            'products'
                          );
                          history.push(
                            `${APP_PATH_PRODUCTS}#candidate-interview-scoring`
                          );
                          handleProductsClose();
                        }}>
                        Explore the scorecards
                      </Button>
                    </Box>
                  </Box>
                </Menu>
              </Grid>
              <Grid item>
                <Typography
                  className={classes.menuTextDropdown}
                  variant="h5"
                  onClick={handleCommunityClick}>
                  Community ▾
                </Typography>
                <Menu
                  anchorEl={communityAnchorEl}
                  open={Boolean(communityAnchorEl)}
                  onClose={handleCommunityClose}
                  className={classes.dropdownMenu}
                  PaperProps={{
                    className: clsx(
                      classes.infoMenuPaper,
                      classes.communityMenuPaper
                    )
                  }}
                  MenuListProps={{ disablePadding: true }}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}>
                  <Box className={classes.infoMenuContent}>
                    <Box className={classes.infoMenuLeft}>
                      <div className={classes.infoMenuHeader}>
                        <Typography
                          variant="h6"
                          className={classes.infoMenuTitle}>
                          Community
                        </Typography>
                        <Typography className={classes.infoMenuSubtitle}>
                          Follow along for hiring advice, product updates, and
                          remote work stories.
                        </Typography>
                      </div>
                      <MenuItem
                        className={classes.infoMenuItem}
                        component="a"
                        href="https://www.instagram.com/remot_ework33/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          trackTopbarClick(
                            'instagram',
                            'https://www.instagram.com/remot_ework33/',
                            'community'
                          );
                          handleCommunityClose();
                        }}>
                        <InstagramIcon className={classes.infoMenuIcon} />
                        <Box>
                          <Typography className={classes.infoMenuItemTitle}>
                            Instagram
                          </Typography>
                          <Typography className={classes.infoMenuItemDesc}>
                            Behind-the-scenes updates and quick hiring tips.
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem
                        className={classes.infoMenuItem}
                        component="a"
                        href="https://www.linkedin.com/company/remote-job-ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          trackTopbarClick(
                            'linkedin',
                            'https://www.linkedin.com/company/remote-job-ai',
                            'community'
                          );
                          handleCommunityClose();
                        }}>
                        <LinkedInIcon className={classes.infoMenuIcon} />
                        <Box>
                          <Typography className={classes.infoMenuItemTitle}>
                            LinkedIn
                          </Typography>
                          <Typography className={classes.infoMenuItemDesc}>
                            Company updates and community insights.
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Box>
                    <Box className={classes.infoMenuRight}>
                      <Typography className={classes.infoMenuKicker}>
                        Stay Connected
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.infoMenuSpotlightTitle}>
                        Join the Remote Job community
                      </Typography>
                      <Typography className={classes.infoMenuSpotlightDesc}>
                        Discover hiring playbooks, product releases, and
                        customer stories from distributed teams.
                      </Typography>
                    </Box>
                  </Box>
                </Menu>
              </Grid>
              <Grid item>
                <Typography
                  className={classes.menuTextDropdown}
                  variant="h5"
                  onClick={handleAboutClick}>
                  About ▾
                </Typography>
                <Menu
                  anchorEl={aboutAnchorEl}
                  open={Boolean(aboutAnchorEl)}
                  onClose={handleAboutClose}
                  className={classes.dropdownMenu}
                  PaperProps={{
                    className: clsx(
                      classes.infoMenuPaper,
                      classes.aboutMenuPaper
                    )
                  }}
                  MenuListProps={{ disablePadding: true }}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}>
                  <Box className={classes.infoMenuContent}>
                    <Box className={classes.infoMenuLeft}>
                      <div className={classes.infoMenuHeader}>
                        <Typography
                          variant="h6"
                          className={classes.infoMenuTitle}>
                          About
                        </Typography>
                        <Typography className={classes.infoMenuSubtitle}>
                          Find quick answers and learn how Remote Job supports
                          candidates and employers.
                        </Typography>
                      </div>
                      <MenuItem
                        className={classes.infoMenuItem}
                        onClick={() => {
                          trackTopbarClick(
                            'candidate_faq',
                            APP_PATH_FAQ_JOB_SEEKERS,
                            'about'
                          );
                          history.push(APP_PATH_FAQ_JOB_SEEKERS);
                          handleAboutClose();
                        }}>
                        <PersonOutlineOutlinedIcon
                          className={classes.infoMenuIcon}
                        />
                        <Box>
                          <Typography className={classes.infoMenuItemTitle}>
                            Candidate FAQ
                          </Typography>
                          <Typography className={classes.infoMenuItemDesc}>
                            Guidance for job seekers using Remote Job.
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem
                        className={classes.infoMenuItem}
                        onClick={() => {
                          trackTopbarClick(
                            'company_faq',
                            APP_PATH_FAQ_EMPLOYERS,
                            'about'
                          );
                          history.push(APP_PATH_FAQ_EMPLOYERS);
                          handleAboutClose();
                        }}>
                        <BusinessOutlinedIcon
                          className={classes.infoMenuIcon}
                        />
                        <Box>
                          <Typography className={classes.infoMenuItemTitle}>
                            Company FAQ
                          </Typography>
                          <Typography className={classes.infoMenuItemDesc}>
                            Answers for hiring teams and recruiters.
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Box>
                    <Box className={classes.infoMenuRight}>
                      <Typography className={classes.infoMenuKicker}>
                        Resources
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.infoMenuSpotlightTitle}>
                        Looking for help?
                      </Typography>
                      <Typography className={classes.infoMenuSpotlightDesc}>
                        Browse the FAQs to get up to speed on applications,
                        subscriptions, and interview workflows.
                      </Typography>
                    </Box>
                  </Box>
                </Menu>
              </Grid>
              <Grid item>
                <RouterLink
                  to={APP_PATH_PRICING}
                  className={classes.menuTextLink}
                  onClick={() => {
                    trackTopbarClick('pricing', APP_PATH_PRICING, 'topbar');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}>
                  <Typography className={classes.menuText} variant="h5">
                    Pricing
                  </Typography>
                </RouterLink>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {isLogin && user ? (
              // Show user profile section if logged in
              <Box
                className={classes.userProfileSection}
                onClick={handleProfileClick}>
                <Avatar
                  className={classes.userAvatar}
                  src={companyLogoUrl || ''}>
                  {!companyLogoUrl &&
                    getUserInitials(
                      user.fullName || user.full_name || user.email
                    )}
                </Avatar>
                <Box className={classes.userInfo}>
                  <Typography className={classes.userName}>
                    {user.fullName || user.full_name || 'User'}
                  </Typography>
                  <Typography className={classes.userSubtext}>
                    {getProfileSubtext()}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Show Sign In and Post Your Job buttons if not logged in
              <Box className={classes.headerActions}>
                <RouterLink
                  to={APP_PATH_LOGIN}
                  style={{ textDecoration: 'none' }}>
                  <Tooltip
                    title="Sign In (Company / Candidate)"
                    arrow
                    placement="bottom">
                    <Button
                      className={classes.signInBtn}
                      onClick={() =>
                        trackTopbarClick('sign_in', APP_PATH_LOGIN, 'topbar')
                      }>
                      Sign In
                    </Button>
                  </Tooltip>
                </RouterLink>
                <RouterLink
                  to={APP_PATH_SIGNUP}
                  style={{ textDecoration: 'none' }}>
                  <Tooltip
                    title="No subscription · One-time payment"
                    arrow
                    placement="bottom">
                    <Button
                      className={classes.headerBtn}
                      variant="contained"
                      onClick={() =>
                        trackTopbarClick(
                          'post_a_job',
                          APP_PATH_SIGNUP,
                          'topbar'
                        )
                      }>
                      Post a Job
                    </Button>
                  </Tooltip>
                </RouterLink>
              </Box>
            )}
          </Grid>
        </Grid>
      ) : (
        // Mobile Layout - Simplified
        <Box className={classes.mobileHeaderSection}>
          <RouterLink to="/" className={classes.mobileLogoLink}>
            <img
              src="/images/logos/logo.png"
              alt={MAIN_APP_NAME}
              className={classes.mobileLogo}
            />
          </RouterLink>
          <Box className={classes.headerActions}>
            {isLogin && user ? (
              <Box
                className={classes.userProfileSection}
                onClick={handleProfileClick}
                style={{ padding: '6px 12px' }}>
                <Avatar
                  className={classes.userAvatar}
                  src={companyLogoUrl || ''}
                  style={{
                    width: '32px',
                    height: '32px',
                    fontSize: '0.875rem'
                  }}>
                  {!companyLogoUrl &&
                    getUserInitials(
                      user.fullName || user.full_name || user.email
                    )}
                </Avatar>
              </Box>
            ) : (
              <>
                <RouterLink
                  to={APP_PATH_LOGIN}
                  style={{ textDecoration: 'none' }}>
                  <Tooltip
                    title="Sign In (Company / Candidate)"
                    arrow
                    placement="bottom">
                    <Button
                      className={classes.signInBtn}
                      size="small"
                      onClick={() =>
                        trackTopbarClick('sign_in', APP_PATH_LOGIN, 'topbar')
                      }>
                      Sign In
                    </Button>
                  </Tooltip>
                </RouterLink>
                <RouterLink
                  to={APP_PATH_SIGNUP}
                  style={{ textDecoration: 'none' }}>
                  <Tooltip
                    title="No subscription · One-time payment"
                    arrow
                    placement="bottom">
                    <Button
                      className={classes.headerBtn}
                      variant="contained"
                      size="small"
                      onClick={() =>
                        trackTopbarClick(
                          'post_a_job',
                          APP_PATH_SIGNUP,
                          'topbar'
                        )
                      }>
                      Post a Job
                    </Button>
                  </Tooltip>
                </RouterLink>
              </>
            )}
          </Box>
        </Box>
      )}
    </header>
  );
};

const MainPublic = (props) => {
  const { children } = props;
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true
  });

  // Hide hero section on Products, FAQ, and Pricing pages
  const showHeroSection =
    location.pathname !== APP_PATH_AI &&
    location.pathname !== APP_PATH_PRODUCTS &&
    location.pathname !== APP_PATH_FAQ_EMPLOYERS &&
    location.pathname !== APP_PATH_FAQ_JOB_SEEKERS &&
    location.pathname !== APP_PATH_PRICING;
  const history = useHistory();
  return (
    <div className={classes.mainPage}>
      <section className={classes.coverSection}>
        <Header isDesktop={isDesktop} />
        {showHeroSection && (
          <div className={classes.coverSectionItems}>
            <Typography
              className={classes.coverSectionContentText}
              variant="h2">
              <TypeAnimation
                sequence={[
                  'Find High-Quality Remote Jobs',
                  2800,
                  'Hire Exceptional Remote Talent',
                  2800,
                  'One Platform for Remote Hiring',
                  2800,
                  'Remote Hiring, Without the Noise',
                  2800
                ]}
                speed={20}
                repeat={Infinity}
              />
            </Typography>
            <Typography
              className={classes.coverSectionTextSubDesc}
              variant="h5">
              Post a job and manage candidates in one simple workspace. Discover
              high-quality remote roles from trusted, remote-first companies.
              All in one place.
            </Typography>
            <Box className={classes.heroCTAContainer}>
              <RouterLink
                to={APP_PATH_SIGNUP}
                style={{ textDecoration: 'none' }}>
                <Button className={classes.primaryCTABtn} variant="outlined">
                  Post a Job & Hire Faster
                </Button>
              </RouterLink>
              <Button
                component="a"
                href="#jobs"
                className={classes.secondaryCTABtn}
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  const target = document.getElementById('jobs');
                  if (target) {
                    target.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }}>
                Find Remote Jobs
              </Button>
            </Box>
          </div>
        )}
      </section>
      {showHeroSection && location.pathname === APP_PATH_ROOT && (
        <section
          className={classes.platformSection}
          aria-labelledby="platform-features-title">
          <div className={classes.platformSectionInner}>
            <Grid container spacing={4} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <Typography
                  className={classes.platformEyebrow}
                  variant="overline"
                  component="p">
                  Platform Features
                </Typography>
                <Typography
                  className={classes.platformTitle}
                  variant="h3"
                  component="h2"
                  id="platform-features-title">
                  Hiring tools included with every job{' '}
                  <span className={classes.platformTitleNoWrap}>post</span>
                </Typography>
                <Typography
                  className={classes.platformSubtitle}
                  variant="subtitle1"
                  component="p">
                  Post once, then manage candidates end-to-end in a single
                  workspace. Everything your team needs to review, interview,
                  and decide faster — all in one place.
                </Typography>
                <Box className={classes.platformFeatures} component="ul">
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Candidate Management
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Drag candidates through Review → Interview → Offer with a
                      visual pipeline.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Application Tracking
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      See every applicant’s status, activity, and timeline in
                      one dashboard.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Built-In Messaging
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Communicate with candidates directly — all conversations
                      stay organized per role.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Interview Scheduling
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Schedule interviews and send automatic confirmations to
                      candidates and interviewers.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography
                      className={clsx(
                        classes.platformFeatureTitle,
                        classes.platformFeatureTitleHighlight
                      )}>
                      Candidate Interview Scoring
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Rate interviews as Very Poor, Poor, Good, or Very Good.
                      Scores stay attached to the candidate card, creating a
                      clear feedback history for easy comparison.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Geo-Location Filtering
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Control job visibility by region to focus on candidates in
                      your target markets.
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.platformCtaRow}>
                  <Button
                    component={RouterLink}
                    to={APP_PATH_SIGNUP}
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.platformPrimaryCta}
                    onClick={() =>
                      trackHomepageCtaClick(
                        'platform_features',
                        'post_a_job',
                        APP_PATH_SIGNUP
                      )
                    }>
                    Post a Job
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.platformSecondaryCta}
                    onClick={() => {
                      trackHomepageCtaClick(
                        'platform_features',
                        'explore_platform_features',
                        APP_PATH_PRODUCTS
                      );
                      history.push(APP_PATH_PRODUCTS);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                    Explore Platform Features
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box className={classes.platformVisual} aria-hidden="true">
                  <Box className={classes.visualTopRow}>
                    <Typography className={classes.visualTitle}>
                      Hiring Workspace
                    </Typography>
                    <Box className={classes.visualBadge}>Pipeline</Box>
                  </Box>
                  <Box className={classes.visualColumns}>
                    <Box className={classes.visualColumn}>
                      <Typography className={classes.visualColumnTitle}>
                        Review
                      </Typography>
                      <Box className={classes.visualMiniCard} />
                      <Box className={classes.visualMiniCard} />
                    </Box>
                    <Box className={classes.visualColumn}>
                      <Typography className={classes.visualColumnTitle}>
                        Interview
                      </Typography>
                      <Box className={classes.visualMiniCard} />
                      <Box className={classes.visualMiniCard} />
                    </Box>
                    <Box className={classes.visualColumn}>
                      <Typography className={classes.visualColumnTitle}>
                        Offer
                      </Typography>
                      <Box className={classes.visualMiniCard} />
                      <Box className={classes.visualMiniCard} />
                    </Box>
                  </Box>
                  <Box className={classes.visualScoreRow}>
                    <Box className={classes.visualScoreChip}>Good</Box>
                    <Box
                      className={clsx(
                        classes.visualScoreChip,
                        classes.visualScoreChipStrong
                      )}>
                      Very Good
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </div>
        </section>
      )}
      {showHeroSection && location.pathname === APP_PATH_ROOT && (
        <section className={classes.aiBannerSection}>
          <div className={classes.platformSectionInner}>
            <Box className={classes.aiBannerCard}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography
                    className={classes.aiBannerBadge}
                    component="span">
                    Coming Soon
                  </Typography>
                  <Typography className={classes.aiBannerTitle} variant="h4">
                    AI-powered tools for faster candidate review
                  </Typography>
                  <Typography className={classes.aiBannerDesc} component="p">
                    We’re building AI features to help recruiters summarize
                    resumes, evaluate fit, and draft outreach messages —
                    directly inside your hiring workflow.
                  </Typography>
                  <Typography className={classes.aiBannerNote} component="p">
                    Available soon. No setup required.
                  </Typography>
                  <Box className={classes.aiBannerList} component="ul">
                    <Box component="li" className={classes.aiBannerItem}>
                      Review candidates faster with structured summaries
                    </Box>
                    <Box component="li" className={classes.aiBannerItem}>
                      Get instant fit insights against job requirements
                    </Box>
                    <Box component="li" className={classes.aiBannerItem}>
                      Draft personalized outreach messages in seconds
                    </Box>
                  </Box>
                  <Box className={classes.platformCtaRow}>
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.aiBannerCta}
                      onClick={() => {
                        trackHomepageCtaClick(
                          'ai_banner',
                          'learn_whats_coming',
                          APP_PATH_AI
                        );
                        history.push(APP_PATH_AI);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}>
                      Learn what’s coming
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </div>
        </section>
      )}
      {showHeroSection && location.pathname === APP_PATH_ROOT && (
        <section
          className={classes.candidateSection}
          aria-labelledby="candidate-features-title">
          <div className={classes.platformSectionInner}>
            <Grid container spacing={4} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <Typography
                  className={classes.platformEyebrow}
                  variant="overline"
                  component="p">
                  For Candidates
                </Typography>
                <Typography
                  className={classes.platformTitle}
                  variant="h3"
                  component="h2"
                  id="candidate-features-title">
                  Find remote jobs. Apply with confidence.
                </Typography>
                <Typography
                  className={classes.platformSubtitle}
                  variant="subtitle1"
                  component="p">
                  Browse remote-friendly roles, apply for free, and communicate
                  directly with hiring teams — all in one place.
                </Typography>
                <Box className={classes.platformFeatures} component="ul">
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Always free for job seekers
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Browse jobs, apply, message companies, and track
                      applications at no cost.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Apply directly to companies
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Your application goes straight to the hiring team — no
                      recruiters in between.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Track every application
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      See all your applications in one dashboard and follow each
                      step of the process.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Messages and interviews in one place
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Companies can message you and send interview invitations
                      directly through the platform.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Privacy first
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Your profile is only visible when you apply. Your data is
                      never shared outside the hiring process.
                    </Typography>
                  </Box>
                  <Box component="li" className={classes.platformFeatureItem}>
                    <Typography className={classes.platformFeatureTitle}>
                      Remote-first, with clear location rules
                    </Typography>
                    <Typography className={classes.platformFeatureDesc}>
                      Apply from anywhere. If a role has location restrictions,
                      they’re shown upfront.
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.platformCtaRow}>
                  <Button
                    component="a"
                    href="#jobs"
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.platformPrimaryCta}
                    onClick={(e) => {
                      trackHomepageCtaClick(
                        'for_candidates',
                        'find_remote_jobs',
                        '#jobs'
                      );
                      e.preventDefault();
                      const target = document.getElementById('jobs');
                      if (target) {
                        target.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }}>
                    Find Remote Jobs
                  </Button>
                  <Button
                    component={RouterLink}
                    to={APP_PATH_SIGNUP}
                    variant="outlined"
                    size="small"
                    className={classes.platformSecondaryCta}
                    onClick={() =>
                      trackHomepageCtaClick(
                        'for_candidates',
                        'create_free_profile',
                        APP_PATH_SIGNUP
                      )
                    }>
                    Create Free Profile
                  </Button>
                </Box>
                <Typography
                  className={classes.platformHelperText}
                  component="p">
                  Have questions? Visit our{' '}
                  <RouterLink
                    to={APP_PATH_FAQ_JOB_SEEKERS}
                    className={classes.inlineLink}>
                    Job Seeker FAQ
                  </RouterLink>{' '}
                  to learn how applying, messaging, and interviews work.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box className={classes.candidateVisual} aria-hidden="true">
                  <Box className={classes.candidateVisualHeader}>
                    <Typography className={classes.candidateVisualTitle}>
                      Candidate Dashboard
                    </Typography>
                    <Box className={classes.candidateVisualTag}>
                      Applications
                    </Box>
                  </Box>
                  <Box className={classes.candidateVisualList}>
                    <Box className={classes.candidateVisualRow}>
                      <Typography className={classes.candidateVisualRowTitle}>
                        Product Designer
                      </Typography>
                      <Box className={classes.candidateVisualRowMeta}>
                        <Box className={classes.candidateVisualBadge}>
                          Applied
                        </Box>
                        <Box
                          className={clsx(
                            classes.candidateVisualBadge,
                            classes.candidateVisualBadgePrimary
                          )}>
                          Interviewing
                        </Box>
                      </Box>
                    </Box>
                    <Box className={classes.candidateVisualRow}>
                      <Typography className={classes.candidateVisualRowTitle}>
                        Frontend Engineer
                      </Typography>
                      <Box className={classes.candidateVisualRowMeta}>
                        <Box className={classes.candidateVisualBadge}>
                          Applied
                        </Box>
                        <Box className={classes.candidateVisualBadge}>
                          Review
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box className={classes.candidateVisualMessage}>
                    New message: Interview invite for Thursday at 10:00 AM.
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </div>
        </section>
      )}
      <Container
        className={clsx({
          [classes.root]: true,
          [classes.shiftContent]: isDesktop
        })}>
        <main className={classes.content}>{children}</main>
      </Container>
      <Footer />
    </div>
  );
};

MainPublic.propTypes = {
  children: PropTypes.node
};

export default MainPublic;
